// src/modules/app-views/ticket-events/pages/Document/tabs/RecordTab/PricingConfigCard.jsx
import { useState } from 'react';
import { Card, Col, Form, Row, Slider, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleRadioButtons from 'components/SimpleComponents/SimpleRadioButtons';

const { Text } = Typography;

const RECALC_OPTIONS = [
  { value: 0.5, label: '30min' },
  { value: 1, label: '1h' },
  { value: 2, label: '2h' },
  { value: 6, label: '6h' },
  { value: 12, label: '12h' },
  { value: 24, label: '24h' },
];

const PricingConfigCard = () => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { locked, form } = useSimpleFormContext();

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [weights, setWeights] = useState({ demandWeight: 40, scarcityWeight: 40, velocityWeight: 20 });

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleWeightChange = (key, newValue) => {
    const remaining = 100 - newValue;
    const others = Object.keys(weights).filter(k => k !== key);
    const currentOthersTotal = others.reduce((sum, k) => sum + weights[k], 0);
    const updated = { ...weights, [key]: newValue };
    if (currentOthersTotal > 0) {
      others.forEach(k => {
        updated[k] = Math.round((weights[k] / currentOthersTotal) * remaining);
      });
      const diff = 100 - Object.values(updated).reduce((s, v) => s + v, 0);
      updated[others[1]] += diff;
    }
    setWeights(updated);
    form.setFieldsValue({
      pricingConfig: {
        ...form.getFieldValue('pricingConfig'),
        demandWeight: updated.demandWeight / 100,
        scarcityWeight: updated.scarcityWeight / 100,
        velocityWeight: updated.velocityWeight / 100,
      },
    });
  };

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <Card title={<div className="text-muted">{translateX('pricing_config')}</div>}>
      <Row gutter={ROW_GUTTER}>
        <Col xs={24} sm={12}>
          <SimpleRadioButtons
            name={['pricingConfig', 'recalcIntervalHours']}
            label={translateX('recalc_interval_hours')}
            options={RECALC_OPTIONS}
          />
        </Col>
        <Col xs={24} sm={12}>
          <SimpleNumber
            name={['pricingConfig', 'criticalWindowHours']}
            label={
              <span>
                {translateX('critical_window_hours')}{' '}
                <Tooltip title={translateX('critical_window_tooltip')}>
                  <InfoCircleOutlined />
                </Tooltip>
              </span>
            }
            min={1}
            max={720}
            suffix={[{ value: 'h', label: 'h' }]}
          />
        </Col>
      </Row>

      <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
        {translateX('pricing_weights')}
      </Text>

      <Row gutter={ROW_GUTTER}>
        <Col xs={24}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8 }}>{`${translateX('demand_weight')} (${weights.demandWeight}%)`}</div>
            <Slider
              min={0} max={100} value={weights.demandWeight}
              disabled={locked}
              onChange={val => handleWeightChange('demandWeight', val)}
            />
          </div>
        </Col>
        <Col xs={24}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8 }}>{`${translateX('scarcity_weight')} (${weights.scarcityWeight}%)`}</div>
            <Slider
              min={0} max={100} value={weights.scarcityWeight}
              disabled={locked}
              onChange={val => handleWeightChange('scarcityWeight', val)}
            />
          </div>
        </Col>
        <Col xs={24}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 8 }}>{`${translateX('velocity_weight')} (${weights.velocityWeight}%)`}</div>
            <Slider
              min={0} max={100} value={weights.velocityWeight}
              disabled={locked}
              onChange={val => handleWeightChange('velocityWeight', val)}
            />
          </div>
        </Col>
      </Row>

      <Text type="secondary" style={{ fontSize: 12 }}>
        {translateX('weights_must_sum_100')}:{' '}
        <strong>{weights.demandWeight + weights.scarcityWeight + weights.velocityWeight}%</strong>
      </Text>

      {/* Hidden fields to store decimal weight values for form submission */}
      <Form.Item name={['pricingConfig', 'demandWeight']} hidden><input /></Form.Item>
      <Form.Item name={['pricingConfig', 'scarcityWeight']} hidden><input /></Form.Item>
      <Form.Item name={['pricingConfig', 'velocityWeight']} hidden><input /></Form.Item>
    </Card>
  );
};

export default PricingConfigCard;
